import { Toast } from '@seiue/ui'
import { isValidPhoneNumber } from '@seiue/util'
import { Button, Form, Input, Modal } from 'antd'
import { useAtom } from 'jotai'
import { FC, useContext, useState } from 'react'
import styled from 'styled-components'

import { UserDomainEnum, activityApi, authApi } from 'packages/sdks-next/growp'
import { rulesMap } from 'packages/utils'

import { activityReflectionStorage, store } from '@/store'

import { VerifyCode } from '../VerifyCode'
import { reminderIdAtom } from '../atom'

import { ActivityLoginContext } from './context'
import { validUrlParams } from './utils'

type LoginDialogProps = {
  visible: boolean
  onChangeVisible: (visible: boolean) => void
  name: string
  hasInviteCode?: boolean
  schoolId?: number
  brandColor: string
  isPreview?: boolean
}

/**
 * 登录弹窗
 *
 * @param param0 - 参数
 * @param param0.visible - 是否显示
 * @param param0.onChangeVisible - 是否显示变化
 * @param param0.name - 活动名称
 * @param param0.hasInviteCode - 是否需要邀请码
 * @param param0.schoolId - 学校id
 * @param param0.brandColor - 品牌色
 * @returns 登录弹窗
 */
export const LoginDialog: FC<LoginDialogProps> = ({
  visible,
  onChangeVisible,
  name,
  hasInviteCode,
  schoolId,
  brandColor,
  isPreview,
}) => {
  const [isRegister, setIsRegister] = useState<boolean>(false)
  // const [agreePolicy, setAgreePolicy] = useState<boolean>(false)

  const [form] = Form.useForm()

  const { activityId } = useContext(ActivityLoginContext)

  const [reminderId, setReminderId] = useAtom(reminderIdAtom)

  return (
    <Modal
      open={visible}
      onCancel={() => {
        onChangeVisible(false)
        form.resetFields()
        setReminderId('')
      }}
      footer={null}
      width={400}
      bodyStyle={{ padding: '48px 32px 32px 32px' }}
    >
      <Title>{name}</Title>
      <Form
        name="activity"
        form={form}
        onFinish={async () => {
          const validParams = await validUrlParams()
          if (!validParams) return

          form
            .validateFields()
            .then(async (values: { phone?: string; inviteCode?: string }) => {
              const formReminderId = form.getFieldValue('reminderId')
              if (!formReminderId) {
                Toast.warning('请输入验证码')
                return
              }

              if (activityId) {
                // if (!agreePolicy) {
                //   Toast.warning('请先勾选同意协议')
                //   return
                // }

                try {
                  const { data: reflection } = await activityApi.loginActivity(
                    Number(activityId),
                    {
                      ...values,
                      reminderId,
                      password: reminderId,
                    }
                  )
                  store.set(activityReflectionStorage, reflection)
                  location.href = '/'
                } catch (e: any) {
                  const { data } = e.response
                  Toast.error(data?.message)
                }
              }
            })
        }}
      >
        <Form.Item
          name="phone"
          rules={[
            ...rulesMap['phone'],
            { required: true, message: '请输入手机号' },
          ]}
        >
          <StyledInput
            placeholder="请输入手机号"
            onChange={async (val) => {
              if (schoolId && hasInviteCode) {
                if (isValidPhoneNumber(val.target.value)) {
                  const { data } = await authApi.checkUserExistByPhone(
                    UserDomainEnum.Informal,
                    {
                      phone: val.target.value,
                      schoolId,
                    },
                    { activityId }
                  )
                  setIsRegister(data)
                } else {
                  setIsRegister(false)
                }
              }
            }}
            brandColor={brandColor}
          />
        </Form.Item>
        <StyledFormItem
          dependencies={['phone']}
          rules={[{ required: true, message: '请输入验证码' }]}
          brandColor={brandColor}
        >
          {({ getFieldValue, setFieldValue }) => {
            const phoneNumber = getFieldValue('phone')
            const reminderId = getFieldValue('reminderId')
            return (
              <VerifyCode
                phoneNumber={phoneNumber}
                onChange={(val) => {
                  setFieldValue('reminderId', val)
                  setReminderId(val)
                }}
                value={reminderId}
                brandColor={brandColor}
                validId={activityId}
                allowPostCode={validUrlParams}
                disabled={isPreview}
              />
            )
          }}
        </StyledFormItem>
        {hasInviteCode ? (
          <Form.Item name="inviteCode">
            <StyledInput
              placeholder={isRegister ? '已注册该活动' : '首次登录请输入邀请码'}
              disabled={isRegister}
              brandColor={brandColor}
            />
          </Form.Item>
        ) : null}
        <StyledFooter>
          <StyledButton
            disabled={!!isPreview}
            type="primary"
            htmlType="submit"
            size="large"
            style={{ backgroundColor: brandColor, borderColor: brandColor }}
          >
            {'登录/注册'}
          </StyledButton>
          {/* 暂时隐藏 */}
          {/* <StyledCheckbox
            onChange={(e) => {
              setAgreePolicy(e.target.checked)
            }}
          >
            {'我已阅读并同意'}
            <StyledLink
              href="/serviceAgreement"
              target="_blank"
            >
              {'服务协议'}
            </StyledLink>
            {'和'}
            <StyledLink
              href="/policy"
              target="_blank"
            >
              {'隐私政策'}
            </StyledLink>
          </StyledCheckbox> */}
        </StyledFooter>
      </Form>
    </Modal>
  )
}

const StyledInput = styled(Input)<{ brandColor?: string }>`
  &&& {
    height: 40px;
    border: 1px solid transparent !important;
    &:hover {
      background-color: ${(props) => props.theme.background._2} !important;
    }
    &:focus {
      background-color: transparent !important;
      border-color: ${(props) =>
        props.brandColor || props.theme.brand._1} !important;
    }
  }
`

const Title = styled.h2`
  margin-bottom: 28px;
  font-size: 28px;
  line-height: 40px;
  text-align: center;
`

const StyledFooter = styled.div`
  margin-top: 64px;
  &&& {
    .ant-btn-primary[disabled] {
      color: #ffffff;
    }
  }
`

const StyledButton = styled(Button)`
  width: 100%;
`

// const StyledCheckbox = styled(Checkbox)`
//   margin-top: 12px;
//   font-size: 12px;
//   line-height: 20px;
// `
// const StyledLink = styled.a`
//   margin: 0 8px;
//   color: ${(props) => props.theme.brand._1};
// `

const StyledFormItem = styled(Form.Item)<{ brandColor?: string }>`
  &&& {
    .ant-input-affix-wrapper {
      border: 1px solid transparent !important;
    }
    .ant-input {
      background-color: transparent !important;
    }
    .ant-input-affix-wrapper-focused {
      background-color: transparent !important;
      border: 1px solid ${(props) => props.brandColor || props.theme.brand._1} !important;
    }
  }
`
